import React from 'react';
import { motion } from 'framer-motion';
import { Code2, Zap, ShieldCheck, Headset, FileSearch, Wind, Sun, Truck, ArrowUpRight } from 'lucide-react';
const competencies = [{
  category: 'Software, SaaS & Web Engineering',
  items: [{
    title: 'Enterprise Drupal Development',
    description: 'Headless and traditional Drupal architecture engineered natively to U.S. Web Design System (USWDS) specifications.',
    icon: Code2,
    tags: ['USWDS', 'Drupal']
  }, {
    title: 'Comply+ SaaS Platform',
    description: 'Proprietary autonomous scanning and remediation engine built to eliminate document, media, and PDF backlogs.',
    icon: Zap,
    tags: ['WCAG 2.1 AA', 'SaaS']
  }, {
    title: 'Secure Software Lifecycle',
    description: 'Custom applications engineered for strict NIST 800-171 and 800-53 compliance from inception to deployment.',
    icon: ShieldCheck,
    tags: ['NIST 800-53', 'DevSecOps']
  }]
}, {
  category: 'BPO & Contact Centers',
  items: [{
    title: 'Omnichannel Call Center',
    description: 'High-availability, tier-structured helpdesk operations for public-facing municipal and agency inquiries.',
    icon: Headset,
    tags: ['Helpdesk', 'Customer Support']
  }, {
    title: 'Data Entry & Verification',
    description: 'Scalable human capital solutions for high-volume record processing and compliance auditing.',
    icon: FileSearch,
    tags: ['Workflow', 'Auditing']
  }]
}, {
  category: 'Sustainable Energy & Environmental',
  items: [{
    title: 'Methane Gas Remediation',
    description: 'Turnkey plugging and abandonment (P&A) operations for orphaned wells to halt methane emissions.',
    icon: Wind,
    tags: ['Environmental', 'P&A']
  }, {
    title: 'Commercial Solar Installation',
    description: 'Engineering, procurement, and construction (EPC) of modular solar arrays and backup micro-grids.',
    icon: Sun,
    tags: ['EPC', 'Solar']
  }]
}, {
  category: 'IT Supply Chain & Logistics',
  items: [{
    title: 'TAA-Compliant Procurement',
    description: 'End-to-end hardware sourcing and secure logistics management meeting federal Trade Agreements Act requirements.',
    icon: Truck,
    tags: ['TAA', 'Logistics']
  }]
}];
export function CompetencyGrid() {
  return <section id="capabilities" className="py-20 bg-slate-50" data-id="element-144">
      <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-145">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6" data-id="element-146">
          <div className="max-w-2xl" data-id="element-147">
            <h2 className="text-federal-blue text-sm font-black tracking-[0.2em] uppercase mb-4" data-id="element-148">
              Core Competencies
            </h2>
            <p className="text-3xl md:text-4xl font-bold text-tech-slate leading-tight" data-id="element-149">
              A Diversified Portfolio of <br data-id="element-150" />
              <span className="text-federal-blue" data-id="element-151">
                Public Sector Solutions.
              </span>
            </p>
          </div>
          <div className="h-px flex-grow bg-slate-200 mx-8 hidden lg:block" data-id="element-152" />
          <p className="text-tech-slate/70 text-sm max-w-xs font-medium" data-id="element-153">
            Engineering resilient infrastructure and autonomous compliance for
            the modern landscape.
          </p>
        </div>

        <div className="space-y-16" data-id="element-154">
          {competencies.map((group, gIdx) => <div key={gIdx} data-id="element-155">
              <h3 className="text-federal-blue font-black text-xs uppercase tracking-widest mb-8 flex items-center gap-3" data-id="element-156">
                <span className="w-8 h-px bg-muted-gold" data-id="element-157" />
                {group.category}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-158">
                {group.items.map((item, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1
            }} className="group bg-white p-8 border border-slate-200 hover:border-federal-blue transition-all duration-300 relative overflow-hidden shadow-sm hover:shadow-xl" data-id="element-159">
                    <div className="absolute top-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-160">
                      <ArrowUpRight className="w-5 h-5 text-muted-gold" data-id="element-161" />
                    </div>
                    <div className="w-14 h-14 bg-slate-50 flex items-center justify-center mb-8 group-hover:bg-federal-blue transition-colors duration-300" data-id="element-162">
                      <item.icon className="w-7 h-7 text-federal-blue group-hover:text-white transition-colors" data-id="element-163" />
                    </div>
                    <h4 className="text-xl font-bold text-federal-blue mb-4" data-id="element-164">
                      {item.title}
                    </h4>
                    <p className="text-tech-slate text-sm leading-relaxed mb-8 opacity-80" data-id="element-165">
                      {item.description}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-auto" data-id="element-166">
                      {item.tags.map((tag, tIdx) => <span key={tIdx} className="text-[10px] font-bold tracking-wider uppercase px-2 py-1 bg-slate-100 text-tech-slate" data-id="element-167">
                          {tag}
                        </span>)}
                    </div>
                  </motion.div>)}
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}